import { FC, Fragment, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'
import { formatDateTime } from '../../../../../../../_metronic/helpers'
import { showConfirmAlert } from '../../../../../../../_metronic/helpers/AlertHelper'
import { Button, InfoAlert } from '../../../../../../components'
import { SolicitacaoExameImagem } from '../../../../atendimento/core/_models'
import { ErroExameImagem, traduzirErroExameImagem } from './erros'
import { formatarNumeroPedido } from './numeroPedido'
import { SituacaoPedidoBadge } from './SituacaoPedidoBadge'
import { ErroExameImagemAlerta } from './ErroExameImagemAlerta'
import { chaveExamesImagem, useCancelarExameImagem } from './useExamesImagem'
import styles from './ExamesImagem.module.css'

type Props = {
  idAtendimento: number
  solicitacoes: SolicitacaoExameImagem[]
  tituloId: string
}

const COLUNAS = 5

export const PedidosExameImagemLista: FC<Props> = ({ idAtendimento, solicitacoes, tituloId }) => {
  const { t } = useT()
  const queryClient = useQueryClient()
  const cancelar = useCancelarExameImagem(idAtendimento)
  const [erro, setErro] = useState<ErroExameImagem | undefined>(undefined)
  // o botão some quando o pedido deixa de aguardar: o foco vai para a lista em vez de cair no body
  const listaRef = useRef<HTMLDivElement>(null)

  const ordenadas = [...solicitacoes].sort((a, b) => b.sequencia - a.sequencia)

  const confirmarCancelamento = async (solicitacao: SolicitacaoExameImagem, numero: string) => {
    const confirmado = await showConfirmAlert({
      title: t('exames_imagem.confirmacao.titulo', { numero }),
      text: t('exames_imagem.confirmacao.texto'),
      confirmButtonText: t('exames_imagem.confirmacao.confirmar'),
      cancelButtonText: t('exames_imagem.confirmacao.voltar'),
      danger: true,
      focusCancel: true,
    })
    if (!confirmado) return
    setErro(undefined)
    cancelar.mutate(solicitacao.id, {
      onSettled: () => listaRef.current?.focus(),
      onError: (error) => {
        const traduzido = traduzirErroExameImagem(error, 'cancelar', t)
        setErro(traduzido)
        if (traduzido.acao === 'recarregarLista') {
          queryClient.invalidateQueries(chaveExamesImagem(idAtendimento))
        }
      },
    })
  }

  return (
    <div ref={listaRef} tabIndex={-1} className={styles.alvoFoco}>
      {erro && <ErroExameImagemAlerta erro={erro} />}
      <div className={styles.rolagem} role='region' aria-labelledby={tituloId} tabIndex={0}>
        <table className={`${styles.tabela} ${styles.tabelaPedidos}`} aria-labelledby={tituloId}>
          <colgroup>
            <col className={styles.colNumero} />
            <col className={styles.colDispositivo} />
            <col className={styles.colData} />
            <col className={styles.colSituacao} />
            <col className={styles.colAcao} />
          </colgroup>
          <thead>
            <tr>
              <th scope='col' className={styles.direita}>
                {t('exames_imagem.pedidos.numero')}
              </th>
              <th scope='col'>{t('exames_imagem.pedidos.dispositivo')}</th>
              <th scope='col'>{t('exames_imagem.pedidos.data')}</th>
              <th scope='col'>{t('exames_imagem.pedidos.situacao')}</th>
              <th scope='col' className={styles.direita}>
                {t('exames_imagem.pedidos.acao')}
              </th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((solicitacao) => {
              const numero = formatarNumeroPedido(
                solicitacao.accessionNumber,
                solicitacao.sequencia
              ).exibicao
              const dispositivo = solicitacao.telekitDispositivo
              const nomeDispositivo =
                dispositivo.descricao || dispositivo.dispositivo?.descricao || dispositivo.aeTitle
              const aguardando = solicitacao.situacao === 'AGUARDANDO'
              const erroWorklist = aguardando && solicitacao.situacaoWorklist === 'ERRO'
              return (
                <Fragment key={solicitacao.id}>
                  <tr>
                    <td className={`${styles.direita} fw-bold`} title={numero}>
                      {numero}
                    </td>
                    <td title={`${nomeDispositivo} — ${dispositivo.telekit.empresa.nome}`}>
                      {nomeDispositivo}
                      <span className={styles.apoio}>{dispositivo.telekit.empresa.nome}</span>
                    </td>
                    <td>{formatDateTime(solicitacao.dataAgendada)}</td>
                    <td>
                      <SituacaoPedidoBadge solicitacao={solicitacao} />
                    </td>
                    <td className={styles.direita}>
                      {aguardando && (
                        <div className='d-flex justify-content-end'>
                          <Button
                            variant='tertiary'
                            size='btn-sm'
                            className='px-3'
                            ariaLabel={t('exames_imagem.cancelar_aria', { numero })}
                            disabled={cancelar.isLoading}
                            onClick={() => confirmarCancelamento(solicitacao, numero)}
                          >
                            {t('exames_imagem.cancelar')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {erroWorklist && (
                    <tr className={styles.linhaAviso}>
                      <td colSpan={COLUNAS}>
                        <InfoAlert variant='danger' size='sm' className='mb-0'>
                          {t('exames_imagem.aviso_worklist_erro', { numero })}
                        </InfoAlert>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
